import DownloadButton from "../DownloadButton";
import ProfilePlaceholder from "../../assets/svgIcons/ProfilePlaceholder.svg";
import "../../styles/components/transactionPrintLayouts/ContractReviewFormLayout.css";

const pickValue = (source, keys, fallback = "") => {
  if (!source) return fallback;

  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  return fallback;
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const normalizeChoice = (value) => {
  if (value === undefined || value === null) return "";
  const normalized = String(value).trim().toLowerCase();
  if (["yes", "y", "true", "1"].includes(normalized)) return "yes";
  if (["no", "n", "false", "0"].includes(normalized)) return "no";
  return normalized;
};

const isChoiceYes = (value) => normalizeChoice(value) === "yes";
const isChoiceNo = (value) => normalizeChoice(value) === "no";

const feasibilityReviewRows = [
  {
    label: "Clarity of drawing / product",
    statusKeys: ["clarity_of_drawing", "clarity_of_drawing_product"],
    remarksKeys: ["clarity_of_drawing_remarks", "clarity_of_drawing_remark"],
  },
  {
    label: "Tooling / Handling equipment",
    statusKeys: ["tooling", "tooling_handling_equipment"],
    remarksKeys: ["tooling_remarks", "tooling_remark"],
  },
  {
    label: "Machinery / equipment",
    statusKeys: ["machinery", "machinery_equipment"],
    remarksKeys: ["machinery_remarks", "machinery_remark"],
  },
  {
    label: "Packing requirements",
    statusKeys: ["packing_requirement", "packaging_requirement"],
    remarksKeys: [
      "packing_requirement_remarks",
      "packaging_requirement_remarks",
    ],
  },
  {
    label: "Transport requirements",
    statusKeys: ["transport_requirement", "transport_requirements"],
    remarksKeys: [
      "transport_requirement_remarks",
      "transport_requirement_remark",
    ],
  },
  {
    label: "Regulatory / statutory requirements",
    statusKeys: ["regulatory", "regulatory_statutory_requirement"],
    remarksKeys: ["regulatory_remarks", "regulatory_remark"],
  },
  {
    label: "Other requirements of customer",
    statusKeys: [
      "other_requirement_of_customer",
      "other_requirements_of_customer",
    ],
    remarksKeys: [
      "other_requirement_of_customer_remarks",
      "other_requirements_of_customer_remarks",
    ],
  },
];

const orderReviewRows = [
  {
    label: "Quotation No / Date",
    keys: ["quotation_no_date", "quotation_no", "quotation_date"],
  },
  {
    label: "Order No / Date",
    keys: ["order_no_date", "order_no", "order_date"],
  },
  { label: "Order Quantity", keys: ["order_quantity", "order_qty"] },
  { label: "Delivery Schedule", keys: ["delivery_schedule"] },
  {
    label: "Transport details",
    keys: ["transport_details", "transport_detail"],
  },
  { label: "Price / Unit", keys: ["price_unit", "price_per_unit"] },
  { label: "Commercial Taxes", keys: ["commercial_taxes", "commercial_tax"] },
  { label: "Others", keys: ["others"] },
];

export const ContractReviewFormLayout = ({
  headDetails = {},
  companyData = {},
}) => {
  let organisationData = {};
  try {
    organisationData = JSON.parse(localStorage.getItem("organisation") || "{}");
  } catch (error) {
    organisationData = {};
  }

  const reportDate = formatDate(
    pickValue(headDetails, ["date", "created_at", "review_date"], new Date()),
  );

  const refNo =
    pickValue(headDetails, ["ref_no", "reference_no", "form_ref_no"]) ||
    "FP/F/MKT/04";
  const revNo = pickValue(headDetails, [
    "rev_no",
    "revision_no",
    "form_rev_no",
  ]) || "04";
  const revDate = formatDate(
    pickValue(headDetails, ["rev_date", "revision_date", "form_rev_date"]) || "06/06/2026",
  );

  const customer = pickValue(headDetails, [
    "customer",
    "party_name",
    "customer_name",
    "company_name",
  ]);
  const partNo = pickValue(headDetails, [
    "part_no",
    "part_number",
    "item_code",
  ]);
  const partName = pickValue(headDetails, [
    "part_name",
    "item_name",
    "product_name",
  ]);
  const drawingRevision = pickValue(headDetails, [
    "drg_rev_no",
    "drg_rev_no_date",
    "drg_rev_no_and_date",
    "dwg_rev_no",
    "drawing_revision",
  ]);
  const enquiryDate = formatDate(
    pickValue(headDetails, ["enquiry_date", "enq_date", "ENQ_DATE", "date"]),
  );

  const reviewComments = pickValue(headDetails, [
    "review_comments",
    "review_comment",
  ]);
  const orderReviewComments = pickValue(headDetails, [
    "order_review_comments",
    "order_review_comment",
  ]);

  const resultOfReview = normalizeChoice(
    pickValue(headDetails, ["result_of_review", "review_result"]),
  );

  const acceptability = normalizeChoice(
    pickValue(headDetails, [
      "acceptability",
      "acceptable_status",
      "acceptance_status",
    ]),
  );

  const reviewedBy = pickValue(headDetails, ["reviewed_by", "created_by"]);
  const approvedBy = pickValue(headDetails, ["approved_by"]);

  const downloadRows = orderReviewRows.map((row, index) => ({
    sl_no: index + 1,
    description: row.label,
    details: pickValue(headDetails, row.keys),
  }));

  const contractReviewTableData = {
    headings: [
      { display_name: "SL.NO", field_name: "sl_no", is_internal: false },
      {
        display_name: "DESCRIPTION",
        field_name: "description",
        is_internal: false,
      },
      { display_name: "DETAILS", field_name: "details", is_internal: false },
    ],
    rows: downloadRows,
  };

  return (
    <div className="contract-review-page">
      <div className="contract-review-card">
        <div className="contract-review-header-row">
          <div className="contract-review-brand">
            <img
              src={organisationData?.logo_url || ProfilePlaceholder}
              alt="Company Logo"
              className="contract-review-logo"
            />
            <div className="contract-review-org-name">
              {organisationData?.name ||
                companyData?.company_name ||
                customer ||
                ""}
            </div>
          </div>
          <div className="contract-review-title-box">CONTRACT REVIEW FORM</div>
          <div className="contract-review-ref-box">
            <div>REF. NO. : {refNo || "-"}</div>
            <div>REV. NO. : {revNo || "-"}</div>
            <div>REV. DATE : {revDate || "-"}</div>
          </div>
        </div>

        <div className="contract-review-main-fields">
          <div className="contract-review-main-field">
            <span>DATE:</span>
            <strong>{reportDate}</strong>
          </div>
          <div className="contract-review-main-field">
            <span>CUSTOMER:</span>
            <strong>{customer}</strong>
          </div>
          <div className="contract-review-main-field">
            <span>PART NO:</span>
            <strong>{partNo}</strong>
          </div>
          <div className="contract-review-main-field">
            <span>PART NAME:</span>
            <strong>{partName}</strong>
          </div>
          <div className="contract-review-main-field">
            <span>DRG. REV. NO / DATE:</span>
            <strong>{drawingRevision}</strong>
          </div>
          <div className="contract-review-main-field">
            <span>ENQUIRY DATE:</span>
            <strong>{enquiryDate}</strong>
          </div>
        </div>

        <table className="contract-review-table">
          <thead>
            <tr>
              <th className="sl-col">SL.NO</th>
              <th>DESCRIPTION</th>
              <th className="yn-col">YES</th>
              <th className="yn-col">NO</th>
              <th className="remarks-col">REMARKS</th>
            </tr>
          </thead>
          <tbody>
            {feasibilityReviewRows.map((row, index) => {
              const statusValue = pickValue(headDetails, row.statusKeys);
              const remarksValue = pickValue(headDetails, row.remarksKeys);
              return (
                <tr key={row.label}>
                  <td className="sl-col">{index + 1}</td>
                  <td>{row.label}</td>
                  <td className="yn-col">
                    {isChoiceYes(statusValue) ? "✓" : ""}
                  </td>
                  <td className="yn-col">
                    {isChoiceNo(statusValue) ? "-" : ""}
                  </td>
                  <td className="remarks-col">{remarksValue || ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="contract-review-comments">
          Review comments: {reviewComments || ""}
        </div>

        <div className="contract-review-result-row">
          <span>Result of review:</span>
          <span>Feasible</span>
          <span
            className={`check-box ${resultOfReview === "feasible" ? "active" : ""}`}
            aria-hidden="true"
          ></span>
          <span>Feasible with modifications</span>
          <span
            className={`check-box ${resultOfReview.includes("modification") ? "active" : ""}`}
            aria-hidden="true"
          ></span>
          <span>not feasible</span>
          <span
            className={`check-box ${resultOfReview === "not feasible" ? "active" : ""}`}
            aria-hidden="true"
          ></span>
        </div>

        <table className="contract-review-table contract-review-order-table">
          <thead>
            <tr>
              <th className="sl-col">SL.NO</th>
              <th>DESCRIPTION</th>
              <th className="remarks-col">DETAILS</th>
            </tr>
          </thead>
          <tbody>
            {orderReviewRows.map((row, index) => (
              <tr key={row.label}>
                <td className="sl-col">{index + 1}</td>
                <td>{row.label}</td>
                <td className="remarks-col">
                  {pickValue(headDetails, row.keys)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="contract-review-approval-block">
          <div className="contract-review-comments">
            Order review comments: {orderReviewComments || ""}
          </div>

          <div className="contract-review-footer-row">
            <div className="contract-review-footer-choice">
              <span>ACCEPTABLE</span>
              <span
                className={`check-box ${acceptability === "acceptable" ? "active" : ""}`}
                aria-hidden="true"
              ></span>
            </div>
            <div className="contract-review-footer-choice">
              <span>ACCEPTABLE WITH MODIFICATIONS</span>
              <span
                className={`check-box ${acceptability.includes("modification") ? "active" : ""}`}
                aria-hidden="true"
              ></span>
            </div>
            <div className="contract-review-footer-choice">
              <span>NOT ACCEPTABLE</span>
              <span
                className={`check-box ${acceptability === "not acceptable" ? "active" : ""}`}
                aria-hidden="true"
              ></span>
            </div>
          </div>

          <div className="contract-review-signature-row">
            <div>REVIEWED BY: {reviewedBy || ""}</div>
            <div>APPROVED BY: {approvedBy || ""}</div>
          </div>
        </div>

        <p className="invoice-system-generated-note">
          This is a system-generated document and does not require a signature.
        </p>

        <div className="invoice-buttons contract-review-actions">
          <DownloadButton
            tableData={contractReviewTableData}
            fileName="contract-review-form"
            downloadFormats={["pdf", "full-page-pdf"]}
            fullPageDownload={true}
            fullPageSelector=".contract-review-page"
          />
        </div>
      </div>
    </div>
  );
};
